import { useEffect, useMemo, useState } from 'react';
import {
  RiArrowGoBackLine,
  RiAttachmentLine,
  RiCheckboxCircleLine,
  RiCheckboxBlankCircleLine,
  RiDeleteBin6Line,
  RiLightbulbFlashLine,
  RiLinksLine,
  RiPlayLine,
  RiPushpin2Line,
  RiSparkling2Line,
  RiStickyNoteLine,
  RiTimeLine,
  RiUserReceivedLine,
} from 'react-icons/ri';
import type { IconType } from 'react-icons';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Separator } from './ui/separator';
import { useStore } from '../state/store';
import { AiModelPicker } from './AiModelPicker';
import type { AiAction } from '../state/domain';
import { cn } from '@/lib/utils';

const KIND_META: Record<AiAction['kind'], { label: string; icon: IconType; color: string }> = {
  bind_entity: { label: '归属线索', icon: RiUserReceivedLine, color: 'var(--color-primary)' },
  link_entities: { label: '建立关系', icon: RiLinksLine, color: 'var(--color-lead)' },
  add_timeline: { label: '补时间线', icon: RiTimeLine, color: 'var(--color-assess)' },
  add_hypothesis: { label: '登记研判', icon: RiLightbulbFlashLine, color: 'var(--color-lead)' },
  pin_to_wall: { label: '上墙', icon: RiPushpin2Line, color: 'var(--color-evidence)' },
  draw_rope: { label: '拉绳', icon: RiAttachmentLine, color: 'var(--color-cord)' },
  create_note: { label: '简报卡', icon: RiStickyNoteLine, color: 'var(--color-primary)' },
};

const PRESETS = ['补全线索关系', '补齐时间线', '为焦点对象补研判', '把已验证线索上墙'];

export function OperatePanel() {
  const aiPlan = useStore(state => state.aiPlan);
  const planning = useStore(state => state.planningActions);
  const applying = useStore(state => state.applyingActions);
  const planActions = useStore(state => state.planActions);
  const applyActions = useStore(state => state.applyActions);
  const rollbackLastApply = useStore(state => state.rollbackLastApply);
  const lastApplyBatch = useStore(state => state.lastApplyBatch);
  const discardPlan = useStore(state => state.discardPlan);
  const meta = useStore(state => state.meta);
  const activeTarget = useStore(state => state.targets.find(t => t.id === state.activeTargetId));

  const [instruction, setInstruction] = useState('');
  const [selected, setSelected] = useState<string[]>([]);

  useEffect(() => {
    setSelected(aiPlan?.actions.map(a => a.id) ?? []);
  }, [aiPlan]);

  const allSelected = useMemo(
    () => Boolean(aiPlan) && selected.length === (aiPlan?.actions.length ?? 0),
    [aiPlan, selected]
  );

  const run = () => planActions(instruction.trim() || undefined);

  return (
    <div className="flex flex-col">
      <div className="flex items-center gap-2 border-b border-border px-3 py-2.5">
        <RiSparkling2Line className="size-3.5 text-[var(--color-lead)]" />
        <span className="text-[12px] font-semibold">AI 操作台</span>
        <div className="ml-auto">
          <AiModelPicker compact />
        </div>
      </div>

      <div className="border-b border-border px-3 py-2">
        <AiModelPicker />
      </div>

      <div className="flex flex-col gap-2.5 p-3">
        <p className="text-[11px] leading-relaxed text-muted-foreground">
          让 AI 先**读现状、出动作**（不直接改数据），你确认后再批量应用。数据类动作走接口，画布类动作（上墙 / 拉绳）
          直接落在关联墙上。
        </p>

        <Textarea
          rows={2}
          value={instruction}
          placeholder={
            activeTarget ? `针对「${activeTarget.name}」要做什么？（可留空自动规划）` : '描述要让 AI 完成的分析操作…'
          }
          onChange={event => setInstruction(event.target.value)}
        />

        <div className="flex flex-wrap gap-1">
          {PRESETS.map(preset => (
            <button
              key={preset}
              type="button"
              onClick={() => setInstruction(preset)}
              className="rounded-full border border-border bg-card px-2 py-[3px] text-[10.5px] text-muted-foreground transition-colors hover:border-primary hover:text-primary"
            >
              {preset}
            </button>
          ))}
        </div>

        {lastApplyBatch && (
          <div className="flex items-center gap-2 rounded-[var(--radius)] border border-[var(--color-evidence)]/30 bg-[var(--color-evidence)]/6 px-2.5 py-2">
            <div className="min-w-0 flex-1">
              <div className="text-[11px] font-medium text-[var(--color-evidence)]">上次应用可撤销</div>
              <div className="truncate text-[10px] text-muted-foreground">
                {new Date(lastApplyBatch.at).toLocaleTimeString('zh-CN', { hour12: false })} ·{' '}
                {lastApplyBatch.labels.length} 项操作
              </div>
            </div>
            <Button size="xs" variant="outline" className="shrink-0" onClick={() => rollbackLastApply()}>
              <RiArrowGoBackLine className="size-3" />
              撤销
            </Button>
          </div>
        )}

        <Button size="sm" disabled={planning} onClick={run}>
          <RiPlayLine className={cn('size-3.5', planning && 'animate-pulse')} />
          {planning ? '规划中…' : '生成动作'}
        </Button>

        {aiPlan && (
          <>
            <Separator />
            <div className="flex items-center gap-1.5 text-[10px] text-muted-foreground">
              <Badge variant={aiPlan.mode === 'remote' ? 'lead' : 'muted'}>
                {aiPlan.mode === 'remote' ? '远端模型' : '本地规划'}
              </Badge>
              {aiPlan.providerError && <span className="truncate">（{aiPlan.providerError}）</span>}
            </div>
            <p className="text-[11.5px] leading-relaxed">{aiPlan.summary}</p>

            <div className="flex items-center gap-1">
              <Button
                size="xs"
                variant="ghost"
                onClick={() => setSelected(allSelected ? [] : aiPlan.actions.map(a => a.id))}
              >
                {allSelected ? '取消全选' : '全选'}
              </Button>
              <Button size="xs" variant="ghost" onClick={() => discardPlan()}>
                <RiDeleteBin6Line className="size-3" />
                放弃
              </Button>
              <span className="ml-auto text-[10px] text-muted-foreground">
                已选 {selected.length}/{aiPlan.actions.length}
              </span>
            </div>

            <div className="flex flex-col gap-1.5">
              {aiPlan.actions.map(action => {
                const meta = KIND_META[action.kind];
                const Icon = meta.icon;
                const checked = selected.includes(action.id);
                return (
                  <button
                    key={action.id}
                    type="button"
                    onClick={() =>
                      setSelected(prev =>
                        checked ? prev.filter(id => id !== action.id) : [...prev, action.id]
                      )
                    }
                    className={cn(
                      'flex gap-2 rounded-[var(--radius)] border p-2 text-left transition-colors',
                      checked ? 'border-primary/50 bg-primary/4' : 'border-border bg-card hover:bg-accent/40'
                    )}
                  >
                    <span className="mt-px shrink-0">
                      {checked ? (
                        <RiCheckboxCircleLine className="size-4 text-primary" />
                      ) : (
                        <RiCheckboxBlankCircleLine className="size-4 text-muted-foreground" />
                      )}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="flex items-center gap-1.5">
                        <Icon className="size-3.5 shrink-0" style={{ color: meta.color }} />
                        <Badge variant="muted" className="shrink-0">
                          {meta.label}
                        </Badge>
                        <span className="truncate text-[11.5px] font-medium">{action.label}</span>
                      </span>
                      <span className="mt-0.5 block text-[10.5px] leading-relaxed text-muted-foreground">
                        {action.reason}
                      </span>
                    </span>
                  </button>
                );
              })}
            </div>

            <Button
              size="sm"
              disabled={applying || selected.length === 0}
              onClick={() => applyActions(selected)}
            >
              <RiPlayLine className={cn('size-3.5', applying && 'animate-pulse')} />
              {applying ? '应用中…' : `应用所选（${selected.length}）`}
            </Button>
          </>
        )}
      </div>
    </div>
  );
}